import { useNichePersonImage, useSubject } from '@/hooks';
import {
  ElectricBolt as ElectricBoltIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  CardMedia,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  DesktopClassic as DesktopClassicIcon,
  Pistol as PistolIcon,
} from 'mdi-material-ui';
import { FlagAvatar } from './FlagAvatar';

export function Warrant({ warrant, onOptionClick }) {
  const subjectType = 'OUTSTANDING_WARRANT';
  const { data: imageSrc } = useNichePersonImage(warrant?.personId);
  const { data: subject } = useSubject(subjectType, warrant?.id, {
    type: subjectType,
    personId: warrant?.id,
  });

  return (
    <Card variant="outlined" sx={{ minWidth: 240 }}>
      <CardHeader
        avatar={<FlagAvatar code="WAA" />}
        title={[warrant?.forenames, warrant?.surname].join(' ').toLowerCase()}
        sx={{ textTransform: 'capitalize' }}
      />
      {imageSrc ? (
        <CardMedia
          component="img"
          sx={{ minHeight: 240, width: '100%' }}
          image={imageSrc}
          alt="Photo of warrant subject"
        />
      ) : (
        <Box
          sx={(theme) => ({
            height: 240,
            width: '100%',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
          })}
        />
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant="body3"
            color="textSecondary"
            sx={{ flexGrow: 1 }}
          >
            {warrant?.occurrenceNumber}
          </Typography>
          <Typography variant="subtitle2">
            {warrant?.time && format(warrant.time, 'dd/MM/yyyy HH:mm')}
          </Typography>
        </Stack>
        {warrant?.offence && (
          <Typography variant="body2" sx={{ p: 0.5 }}>
            {warrant.offence}
          </Typography>
        )}
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
          <Tooltip
            title={
              subject?.pncCirculated
                ? 'Circulated on PNC'
                : 'Not circulated on PNC'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                warrant?.id,
                subject?.code,
                'pncCirculated',
              )}
              color={subject?.pncCirculated ? 'primary' : 'default'}
            >
              <DesktopClassicIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.socialMediaCirculated
                ? 'Circulated on social media'
                : 'Not circulated on social media'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                warrant?.id,
                subject?.code,
                'socialMediaCirculated',
              )}
              color={subject?.socialMediaCirculated ? 'primary' : 'default'}
            >
              <FacebookIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.taser ? 'Taser officers only' : 'No Taser authority'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                warrant?.id,
                subject?.code,
                'taser',
              )}
              color={subject?.taser ? 'primary' : 'default'}
            >
              <ElectricBoltIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.firearms
                ? 'Firearms officers only'
                : 'No Firearms authority'
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                warrant?.id,
                subject?.code,
                'firearms',
              )}
              color={subject?.firearms ? 'primary' : 'default'}
            >
              <PistolIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
